/**
 * 화면 행동 기록(노출·클릭 등) 큐 — 묶어 보내기·오프라인 보관·세션. docs/unified-log-plan.md 3.1.
 *
 * 브라우저·앱 API(sendBeacon·localStorage·Capacitor)는 밖에서 넣어 준다 — 이 파일은 순수 로직이라 node --test 로 바로 돈다.
 * 실제 연결은 `common/analytics.ts`.
 *
 * - 메모리 큐에 쌓아 ① [FLUSH_COUNT]건이 되거나 ② [FLUSH_INTERVAL_MS] 가 지나거나 ③ 화면이 숨겨질 때 한 번에 보낸다.
 * - 한 묶음 최대 [MAX_BATCH_EVENTS]건·[MAX_BATCH_BYTES] — 넘으면 여러 묶음으로 나눈다.
 * - 보내기에 실패하거나 오프라인이면 저장소(`mc_evq`)에 최대 [MAX_STORED]건·[STORED_TTL_MS] 까지 두었다가 다음 실행·온라인 복귀 때 보낸다.
 *   넘치면 오래된 노출부터 버린다(클릭·전환 우선).
 * - 실패는 늘 조용히 — 화면 동작을 막지 않는다.
 */

export type UiTarget = { kind: string; id: string; pos?: number };

/** 큐에 들어가는 이벤트 하나 — 서버 `UiEventInput` 과 같은 칸 */
export interface UiEvent {
    eid: string;
    type: string;
    ts: number;
    sid?: string;
    path?: string;
    screen?: string;
    section?: string;
    sectionType?: string;
    target?: UiTarget;
    targets?: UiTarget[];
    pos?: number;
    listKey?: string;
    props?: Record<string, string | number | boolean | Array<string | number>>;
}

/** 묶음에 한 번만 싣는 칸 */
export interface UiBatchMeta {
    deviceId?: string;
    appPlatform?: string;
    clientOs?: string;
    appVersion?: string;
    webBuild?: string;
}

/** 저장소(localStorage·sessionStorage 와 같은 모양) */
export interface KeyValueStore {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
}

/**
 * 묶음 한 개 보내기.
 * @param beacon true 면 화면이 닫히는 중(sendBeacon) — 결과를 기다릴 수 없다
 * @returns 보냈으면 true(beacon 은 브라우저가 받아 줬으면 true)
 */
export type UiSender = (body: string, beacon: boolean) => boolean | Promise<boolean>;

export const FLUSH_COUNT = 20;
export const FLUSH_INTERVAL_MS = 5_000;
export const MAX_BATCH_EVENTS = 50;
export const MAX_BATCH_BYTES = 32 * 1024;
export const MAX_STORED = 200;
export const STORED_TTL_MS = 24 * 60 * 60 * 1000;
export const STORED_KEY = 'mc_evq';

export const SESSION_KEY = 'mc_sid';
export const SESSION_IDLE_MS = 30 * 60 * 1000;

const byteLength = (text: string): number => {
    try {
        return new TextEncoder().encode(text).length;
    } catch {
        return text.length * 3;
    }
};

/** 임의 id — eid·세션·목록 표시용(서버 형식: 영숫자·_·- 8~64자) */
export function randomId(): string {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    } catch { /* ignore */ }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * 세션 id — 저장소(sessionStorage)에 `{id, last}` 로 둔다. 마지막 행동 뒤 [SESSION_IDLE_MS] 가 지나면 새로 만든다.
 * 저장소가 없으면 메모리 값으로.
 */
export function createSession(store: KeyValueStore | null, now: () => number = Date.now): () => string {
    let memory: { id: string; last: number } | null = null;
    return (): string => {
        const current = now();
        let saved: { id: string; last: number } | null = memory;
        try {
            const raw = store?.getItem(SESSION_KEY);
            if (raw) saved = JSON.parse(raw) as { id: string; last: number };
        } catch { /* ignore */ }
        const next = saved && typeof saved.id === 'string' && current - saved.last < SESSION_IDLE_MS
            ? { id: saved.id, last: current }
            : { id: randomId(), last: current };
        memory = next;
        try {
            store?.setItem(SESSION_KEY, JSON.stringify(next));
        } catch { /* ignore */ }
        return next.id;
    };
}

/** 이벤트 목록을 [MAX_BATCH_EVENTS]건·[MAX_BATCH_BYTES] 안쪽 묶음 본문들로 나눈다. 혼자서 상한을 넘는 이벤트는 버린다 */
export function splitBatches(events: UiEvent[], meta: UiBatchMeta): { body: string; events: UiEvent[] }[] {
    const out: { body: string; events: UiEvent[] }[] = [];
    let chunk: UiEvent[] = [];
    const bodyOf = (list: UiEvent[]): string => JSON.stringify({ ...meta, events: list });
    for (const event of events) {
        const candidate = [...chunk, event];
        if (candidate.length <= MAX_BATCH_EVENTS && byteLength(bodyOf(candidate)) <= MAX_BATCH_BYTES) {
            chunk = candidate;
            continue;
        }
        if (chunk.length > 0) out.push({ body: bodyOf(chunk), events: chunk });
        chunk = byteLength(bodyOf([event])) <= MAX_BATCH_BYTES ? [event] : [];
    }
    if (chunk.length > 0) out.push({ body: bodyOf(chunk), events: chunk });
    return out;
}

/**
 * 저장할 목록 정리 — 24시간 지난 것을 빼고, [MAX_STORED] 를 넘으면 오래된 노출부터, 그래도 넘으면 오래된 것부터 버린다.
 * eid 가 같은 것은 하나만(재전송 대비).
 */
export function trimStored(events: UiEvent[], now: number): UiEvent[] {
    const seen = new Set<string>();
    let list = events.filter((event) => {
        if (!event || typeof event.ts !== 'number' || now - event.ts > STORED_TTL_MS || seen.has(event.eid)) return false;
        seen.add(event.eid);
        return true;
    });
    if (list.length <= MAX_STORED) return list;
    let excess = list.length - MAX_STORED;
    list = list.filter((event) => {
        if (excess > 0 && event.type === 'impression') {
            excess--;
            return false;
        }
        return true;
    });
    return list.length > MAX_STORED ? list.slice(list.length - MAX_STORED) : list;
}

export interface UiEventQueueOptions {
    send: UiSender;
    meta: () => UiBatchMeta;
    store: KeyValueStore | null;
    isOnline: () => boolean;
    now?: () => number;
    setTimer?: (fn: () => void, ms: number) => unknown;
    clearTimer?: (handle: unknown) => void;
}

export interface UiEventQueue {
    push: (event: UiEvent) => void;
    /** 지금 보낸다. beacon=true 는 화면이 숨겨지거나 닫힐 때 */
    flush: (beacon?: boolean) => Promise<void>;
    /** 메모리에 쌓인 건수(테스트·점검용) */
    size: () => number;
    /** 저장소에 남은 기록까지 지운다(수집이 꺼졌을 때) */
    clear: () => void;
}

export function createUiEventQueue(options: UiEventQueueOptions): UiEventQueue {
    const now = options.now ?? Date.now;
    const setTimer = options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
    const clearTimer = options.clearTimer ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>));
    let memory: UiEvent[] = [];
    let timer: unknown = null;
    let sending = false;

    const readStored = (): UiEvent[] => {
        try {
            const raw = options.store?.getItem(STORED_KEY);
            const parsed: unknown = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? (parsed as UiEvent[]) : [];
        } catch {
            return [];
        }
    };

    const writeStored = (events: UiEvent[]): void => {
        try {
            const trimmed = trimStored(events, now());
            if (trimmed.length === 0) options.store?.removeItem(STORED_KEY);
            else options.store?.setItem(STORED_KEY, JSON.stringify(trimmed));
        } catch { /* 저장 공간 부족 등 — 버린다 */ }
    };

    const schedule = (): void => {
        if (timer !== null) return;
        timer = setTimer(() => {
            timer = null;
            void flush(false);
        }, FLUSH_INTERVAL_MS);
    };

    const flush = async (beacon = false): Promise<void> => {
        if (timer !== null) {
            clearTimer(timer);
            timer = null;
        }
        if (sending && !beacon) return;
        const stored = readStored();
        const pending = [...stored, ...memory];
        memory = [];
        if (pending.length === 0) return;
        if (!options.isOnline()) {
            writeStored(pending);
            return;
        }
        // 보내는 동안 저장소를 비워 둔다 — 실패한 것만 다시 넣는다(같은 기록을 두 번 보내지 않게)
        if (stored.length > 0) writeStored([]);
        sending = true;
        const failed: UiEvent[] = [];
        try {
            for (const batch of splitBatches(trimStored(pending, now()), options.meta())) {
                let ok = false;
                try {
                    ok = await options.send(batch.body, beacon);
                } catch {
                    ok = false;
                }
                if (!ok) failed.push(...batch.events);
            }
        } finally {
            sending = false;
        }
        if (failed.length > 0) writeStored([...readStored(), ...failed]);
    };

    const push = (event: UiEvent): void => {
        memory.push(event);
        if (memory.length >= FLUSH_COUNT) void flush(false);
        else schedule();
    };

    const clear = (): void => {
        memory = [];
        if (timer !== null) {
            clearTimer(timer);
            timer = null;
        }
        try {
            options.store?.removeItem(STORED_KEY);
        } catch { /* ignore */ }
    };

    return { push, flush, size: () => memory.length, clear };
}

/**
 * 노출 모으기 — 같은 (화면·섹션·목록 표시) 노출을 [windowMs] 동안 모아 노출 묶음 한 건(targets)으로 낸다.
 */
export function createImpressionBatcher(
    emit: (group: { screen?: string; section?: string; sectionType?: string; listKey?: string; targets: UiTarget[] }) => void,
    windowMs = 1_000,
    setTimer: (fn: () => void, ms: number) => unknown = (fn, ms) => setTimeout(fn, ms),
): { add: (scope: { screen?: string; section?: string; sectionType?: string; listKey?: string }, target: UiTarget) => void; drain: () => void } {
    const groups = new Map<string, { scope: { screen?: string; section?: string; sectionType?: string; listKey?: string }; targets: UiTarget[] }>();
    let timer: unknown = null;

    const drain = (): void => {
        timer = null;
        groups.forEach(({ scope, targets }) => {
            for (let i = 0; i < targets.length; i += MAX_BATCH_EVENTS) {
                emit({ ...scope, targets: targets.slice(i, i + MAX_BATCH_EVENTS) });
            }
        });
        groups.clear();
    };

    const add = (scope: { screen?: string; section?: string; sectionType?: string; listKey?: string }, target: UiTarget): void => {
        const key = [scope.screen, scope.section, scope.sectionType, scope.listKey].join('|');
        const group = groups.get(key) ?? { scope, targets: [] };
        group.targets.push(target);
        groups.set(key, group);
        if (timer === null) timer = setTimer(drain, windowMs);
    };

    return { add, drain };
}

/**
 * 노출 중복 제거 — 같은 목록 표시(listKey) 안에서 같은 대상은 한 번만. 오래된 것부터 [max] 개까지만 기억한다.
 * 뒤로가기로 목록이 복원되면 같은 listKey 를 다시 쓰므로 다시 세지 않는다.
 */
export function createSeenSet(max = 5_000): { markIfNew: (key: string) => boolean } {
    const seen = new Set<string>();
    return {
        markIfNew: (key: string): boolean => {
            if (seen.has(key)) return false;
            seen.add(key);
            if (seen.size > max) {
                const oldest = seen.values().next().value;
                if (oldest !== undefined) seen.delete(oldest);
            }
            return true;
        },
    };
}
